import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import Dropdown, {
  DropdownEventEnum,
} from "@booking/components/compounds/forms/Dropdown";
import TextInput, {
  TextInputTypeEnum,
  TextInputEventEnum,
  TextInputAutocompleteEnum,
} from "@booking/components/compounds/forms/TextInput";
import CheckboxGroup from "@booking/components/containers/forms/CheckboxGroup";
import Fieldset from "@booking/components/containers/forms/Fieldset";
import { ButtonActionEnum, SizeEnum } from "@booking/enums";
import { useSubscribe } from "@booking/hooks/usePubSub";
import React, { useState } from "react";

import { DialingCode } from "@/components/compounds/forminputs/DialingCode";
import { CheckboxInputEventEnum } from "@/components/compounds/forms/CheckboxInput";
import { ColorEnum } from "@/enums";
import type { PubSubData } from "@/types";

import styles from "./EnquiryForm.module.css";
import modalStyles from "./EnquiryModal.module.css";

interface EnquiryPersonalFormProps {
  onSubmit: (personalDetails: EnquiryPersonalFormData) => void;
  onBack?: () => void;
  initialData?: Partial<EnquiryPersonalFormData>;
  isSubmitting?: boolean;
  summaryInfo?: {
    destination: string;
    date: string;
    nights: string;
    rounds: string;
  };
}

export interface EnquiryPersonalFormData {
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  dialing_code: string;
  mobile: string;
  additional_information: string;
  receive_offers: boolean;
}

const initialFormData: EnquiryPersonalFormData = {
  title: "Mr",
  first_name: "",
  last_name: "",
  email: "",
  dialing_code: "+44",
  mobile: "",
  additional_information: "",
  receive_offers: true,
};

const titleOptions = [
  { value: "Mr", text: "Mr" },
  { value: "Mrs", text: "Mrs" },
  { value: "Miss", text: "Miss" },
  { value: "Ms", text: "Ms" },
  { value: "Dr", text: "Dr" },
];

// Input field names
const INPUTS = {
  TITLE: "title",
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  EMAIL: "email",
  DIALING_CODE: "dialing_code",
  MOBILE: "mobile",
  ADDITIONAL_INFORMATION: "additional_information",
  RECEIVE_OFFERS: "receive_offers",
};

function EnquiryPersonalForm(props: EnquiryPersonalFormProps) {
  const {
    onSubmit,
    onBack,
    initialData = {},
    isSubmitting = false,
    summaryInfo,
  } = props;

  const [formData, setFormData] = useState<EnquiryPersonalFormData>({
    ...initialFormData,
    ...initialData,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isFormReady, setIsFormReady] = useState(false);

  function updateControl(
    data: PubSubData & {
      context: keyof EnquiryPersonalFormData;
    },
  ) {
    updateForm({ [data.context]: data.value });
  }

  function updateForm(data: Partial<EnquiryPersonalFormData>) {
    setFormData({ ...formData, ...data });

    // Clear errors for updated fields
    const updatedFields = Object.keys(data);
    if (updatedFields.some((field) => errors[field])) {
      const newErrors = { ...errors };
      updatedFields.forEach((field) => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      setErrors(newErrors);
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = "Valid email is required";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormReady(true);

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  function isControlInvalid(key: keyof EnquiryPersonalFormData) {
    return isFormReady && !!errors[key];
  }

  // Subscribe to form events
  useSubscribe(TextInputEventEnum.CHANGE, updateControl, [
    INPUTS.FIRST_NAME,
    INPUTS.LAST_NAME,
    INPUTS.EMAIL,
    INPUTS.MOBILE,
    INPUTS.ADDITIONAL_INFORMATION,
  ]);

  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [INPUTS.TITLE]);

  useSubscribe(CheckboxInputEventEnum.CHANGE, updateControl, [
    INPUTS.RECEIVE_OFFERS,
  ]);

  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [INPUTS.DIALING_CODE]);

  const renderSummary = () => {
    if (!summaryInfo) return null;

    return (
      <div className={styles.summaryHeader}>
        <h3 className={styles.summaryTitle}>{summaryInfo.destination}</h3>
        <p className={styles.summaryDetails}>
          {summaryInfo.nights} night, {summaryInfo.rounds} round on{" "}
          {summaryInfo.date}
          {onBack && (
            <button onClick={onBack} className={styles.changeButton}>
              (change)
            </button>
          )}
        </p>
      </div>
    );
  };

  return (
    <>
      {renderSummary()}
      <div className={modalStyles.modalFormContainer}>
        <form onSubmit={handleSubmit} className={styles.horizontalForm}>
          <div className={styles.formSection}>
            <Fieldset
              color={ColorEnum.SLATE}
              legend={"Your details"}
              legendBackground={false}
              noBorder
              noLegend
              noPadding
            >
              <div className={styles.formRow}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.EMAIL}>Email</label>
                </div>
                <div className={styles.formInput}>
                  <TextInput
                    autocomplete={TextInputAutocompleteEnum.EMAIL}
                    invalid={isControlInvalid(
                      INPUTS.EMAIL as keyof EnquiryPersonalFormData,
                    )}
                    name={INPUTS.EMAIL}
                    placeholder={"E.g. lee.westwood@yourgolftravel.com"}
                    required
                    type={TextInputTypeEnum.EMAIL}
                    value={formData.email}
                    noLabel
                    label="Email"
                  />
                  {errors.email && (
                    <div className={styles.errorMessage}>{errors.email}</div>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.TITLE}>Title</label>
                </div>
                <div className={styles.formInput}>
                  <div className={styles.titleRow}>
                    <Dropdown
                      className={styles.title}
                      custom
                      name={INPUTS.TITLE}
                      options={titleOptions}
                      required
                      value={formData.title}
                      noLabel
                      label="Title"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.FIRST_NAME}>First name</label>
                </div>
                <div className={styles.formInput}>
                  <TextInput
                    autocomplete={TextInputAutocompleteEnum.GIVEN_NAME}
                    invalid={isControlInvalid(
                      INPUTS.FIRST_NAME as keyof EnquiryPersonalFormData,
                    )}
                    label="First name"
                    name={INPUTS.FIRST_NAME}
                    placeholder={"E.g. Lee"}
                    required
                    noLabel
                    value={formData.first_name}
                  />
                  {errors.first_name && (
                    <div className={styles.errorMessage}>
                      {errors.first_name}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.LAST_NAME}>Last name</label>
                </div>
                <div className={styles.formInput}>
                  <TextInput
                    autocomplete={TextInputAutocompleteEnum.FAMILY_NAME}
                    invalid={isControlInvalid(
                      INPUTS.LAST_NAME as keyof EnquiryPersonalFormData,
                    )}
                    name={INPUTS.LAST_NAME}
                    placeholder={"E.g. Westwood"}
                    required
                    value={formData.last_name}
                    noLabel
                    label="Last name"
                  />
                  {errors.last_name && (
                    <div className={styles.errorMessage}>
                      {errors.last_name}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.MOBILE}>Mobile number</label>
                </div>
                <div className={styles.formInput}>
                  <div className={styles.phoneInputGroup}>
                    <DialingCode
                      id={INPUTS.DIALING_CODE}
                      validityFn={() =>
                        isControlInvalid(
                          INPUTS.MOBILE as keyof EnquiryPersonalFormData,
                        )
                      }
                      value={formData.dialing_code}
                    />

                    <TextInput
                      autocomplete={TextInputAutocompleteEnum.TEL}
                      className={styles.telephone}
                      invalid={isControlInvalid(
                        INPUTS.MOBILE as keyof EnquiryPersonalFormData,
                      )}
                      name={INPUTS.MOBILE}
                      noLabel
                      placeholder={"0000 000000"}
                      required
                      type={TextInputTypeEnum.TEL}
                      value={formData.mobile}
                      label="Mobile number"
                    />
                  </div>
                  {errors.mobile && (
                    <div className={styles.errorMessage}>{errors.mobile}</div>
                  )}
                </div>
              </div>

              <div className={`${styles.formRow} ${styles.verticalFormRow}`}>
                <div className={styles.formLabel}>
                  <label htmlFor={INPUTS.ADDITIONAL_INFORMATION}>
                    Additional info
                  </label>
                </div>
                <div className={`${styles.formInput} ${styles.fullWidth}`}>
                  <textarea
                    name={INPUTS.ADDITIONAL_INFORMATION}
                    id={INPUTS.ADDITIONAL_INFORMATION}
                    placeholder="Questions? Special requests? Let us know about your date of travel, group size, tee times, room types, etc."
                    value={formData.additional_information}
                    onChange={(e) =>
                      updateForm({
                        [INPUTS.ADDITIONAL_INFORMATION]: e.target.value,
                      })
                    }
                    className={styles.textarea}
                    rows={4}
                  />
                </div>
              </div>
            </Fieldset>

            <div className={styles.formRow}>
              <CheckboxGroup
                className={styles.checkboxes}
                color={ColorEnum.SLATE}
                data={[
                  {
                    children: [
                      <p key={"signup"}>
                        Sign up to get member offers from us. By signing up you
                        agree to our{" "}
                        <Button
                          color={ColorEnum.BLUE}
                          href="https://www.yourgolftravel.com/privacy"
                          target="_blank"
                          variant={ButtonVariantEnum.PLAIN}
                        >
                          Privacy Policy
                        </Button>
                        .
                      </p>,
                    ],
                    checked: formData.receive_offers,
                    name: INPUTS.RECEIVE_OFFERS,
                    value: "on",
                  },
                ]}
                legend={"Email signup"}
                name={"email-signup"}
                noBorder
                noLegend
              />
            </div>

            <div className={styles.formActions}>
              <Button
                color={ColorEnum.DARKGREEN}
                disabled={isSubmitting}
                size={SizeEnum.LARGE}
                type={ButtonActionEnum.SUBMIT}
                variant={ButtonVariantEnum.SOLID}
              >
                {isSubmitting ? "Sending..." : "ENQUIRE NOW"}
              </Button>
            </div>

            <div className={styles.callInfo}>
              <p>
                Speak with someone who's been there! Call us on{" "}
                <a href="tel:08000436644" className={styles.link}>
                  0800 043 6644
                </a>{" "}
                from 8:30AM today
              </p>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default EnquiryPersonalForm;
export { EnquiryPersonalForm, type EnquiryPersonalFormProps };
